/**
 * SFGE Dashboard - Main Application
 * 
 * Symbiotic Foresight Intelligence Engine
 * Crisis Prediction & Strategic Analysis Visualization
 * 
 * @author Steven Brandon
 * @version 1.0.0
 */

import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { 
  AlertTriangle, Activity, Globe, Crosshair, Shield, 
  TrendingUp, FileText, Settings, Menu, X, Bell,
  Zap, Target, Map, BarChart3
} from 'lucide-react';

// Views
import DashboardHome from './views/DashboardHome';
import CrisisPrediction from './views/CrisisPrediction';
import StrategicAssessment from './views/StrategicAssessment';
import ScenarioAnalysis from './views/ScenarioAnalysis';
import Wargaming from './views/Wargaming';
import Monitoring from './views/Monitoring';
import Reports from './views/Reports';
import SRDStrategy from './views/SRDStrategy';

// Hooks
import { useSFGE, useAlerts } from './hooks/useSFGE';

// API Configuration
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [systemStatus, setSystemStatus] = useState(null);
  const { alerts, unreadCount } = useAlerts(API_URL);

  useEffect(() => {
    // Fetch system status
    fetch(`${API_URL}/status`)
      .then(res => res.json())
      .then(setSystemStatus)
      .catch(console.error);
  }, []);

  const navigation = [
    { name: 'Dashboard', path: '/', icon: Activity },
    { name: 'Crisis Prediction', path: '/crisis', icon: AlertTriangle },
    { name: 'Strategic Assessment', path: '/strategic', icon: Globe },
    { name: 'Scenario Analysis', path: '/scenarios', icon: Target },
    { name: 'Wargaming', path: '/wargame', icon: Crosshair },
    { name: 'Monitoring', path: '/monitoring', icon: Zap },
    { name: 'SRD Strategy', path: '/srd', icon: TrendingUp },
    { name: 'Reports', path: '/reports', icon: FileText },
  ];

  return (
    <BrowserRouter>
      <div className="min-h-screen bg-slate-900 text-white flex">
        {/* Sidebar */}
        <aside className={`
          ${sidebarOpen ? 'w-64' : 'w-20'} 
          bg-slate-800 border-r border-slate-700 
          transition-all duration-300 flex flex-col
        `}>
          {/* Logo */}
          <div className="h-16 flex items-center px-4 border-b border-slate-700">
            <Shield className="w-8 h-8 text-cyan-500" />
            {sidebarOpen && (
              <div className="ml-3">
                <div className="font-bold text-lg">SFGE</div>
                <div className="text-xs text-slate-400">Foresight Engine</div>
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-4">
            {navigation.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `
                  flex items-center px-4 py-3 mx-2 rounded-lg
                  transition-colors duration-200
                  ${isActive 
                    ? 'bg-cyan-500/20 text-cyan-400' 
                    : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                  }
                `}
              >
                <item.icon className="w-5 h-5" />
                {sidebarOpen && <span className="ml-3">{item.name}</span>}
              </NavLink>
            ))}
          </nav>

          {/* System Status */}
          {sidebarOpen && systemStatus && (
            <div className="p-4 border-t border-slate-700">
              <div className="text-xs text-slate-400 mb-2">System Status</div>
              <div className="flex items-center">
                <div className={`w-2 h-2 rounded-full mr-2 ${
                  systemStatus.initialized ? 'bg-green-500' : 'bg-yellow-500'
                }`} />
                <span className="text-sm">
                  {systemStatus.initialized ? 'Operational' : 'Initializing'}
                </span>
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Mode: {systemStatus.mode?.toUpperCase()}
              </div>
            </div>
          )}

          {/* Toggle */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-4 border-t border-slate-700 hover:bg-slate-700"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </aside>

        {/* Main Content */}
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <header className="h-16 bg-slate-800 border-b border-slate-700 flex items-center justify-between px-6">
            <h1 className="text-xl font-semibold">
              Symbiotic Foresight Intelligence Engine
            </h1>
            
            <div className="flex items-center space-x-4">
              {/* Alerts */}
              <button className="relative p-2 rounded-lg hover:bg-slate-700">
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </button>
              
              {/* Settings */}
              <button className="p-2 rounded-lg hover:bg-slate-700">
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </header>

          {/* Page Content */}
          <main className="flex-1 overflow-auto p-6">
            <Routes>
              <Route path="/" element={<DashboardHome apiUrl={API_URL} />} />
              <Route path="/crisis" element={<CrisisPrediction apiUrl={API_URL} />} />
              <Route path="/strategic" element={<StrategicAssessment apiUrl={API_URL} />} />
              <Route path="/scenarios" element={<ScenarioAnalysis apiUrl={API_URL} />} />
              <Route path="/wargame" element={<Wargaming apiUrl={API_URL} />} />
              <Route path="/monitoring" element={<Monitoring apiUrl={API_URL} />} />
              <Route path="/srd" element={<SRDStrategy apiUrl={API_URL} />} />
              <Route path="/reports" element={<Reports apiUrl={API_URL} />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
        </div>
      </div>
    </BrowserRouter>
  );
}

export default App;
